import React, { useState, useEffect } from "react";
import "../css/Tracker.css";

const STORAGE_KEY = "kk_tracker_entries";

const Tracker = () => {
  const [kidInput, setKidInput] = useState("");
  const [adultInput, setAdultInput] = useState("");
  const [entries, setEntries] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [showResetModal, setShowResetModal] = useState(false);
  const [isEditingId, setIsEditingId] = useState(null);
  const [editKids, setEditKids] = useState(0);
  const [editAdults, setEditAdults] = useState(0);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  }, [entries]);

  const handleAddEntry = () => {
    const kids = parseInt(kidInput, 10) || 0;
    const adults = parseInt(adultInput, 10) || 0;

    if (kids === 0 && adults === 0) {
      alert("Enter at least one kid or adult count.");
      return;
    }

    const now = new Date();
    const timeString = now.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const newEntry = {
      id: now.getTime(),
      time: timeString,
      kids,
      adults,
    };

    setEntries((prev) => [...prev, newEntry]);
    setKidInput("");
    setAdultInput("");
  };

  const handleReset = () => {
    setEntries([]);
    localStorage.removeItem(STORAGE_KEY);
    setShowResetModal(false);
  };

  const handleEdit = (entry) => {
    setIsEditingId(entry.id);
    setEditKids(entry.kids);
    setEditAdults(entry.adults);
  };

  const handleSaveEdit = (id) => {
    const updatedEntries = entries.map((entry) =>
      entry.id === id
        ? {
            ...entry,
            kids: parseInt(editKids, 10) || 0,
            adults: parseInt(editAdults, 10) || 0,
          }
        : entry
    );
    setEntries(updatedEntries);
    setIsEditingId(null);
  };

  const handleCancelEdit = () => {
    setIsEditingId(null);
  };

  const handleDelete = (id) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const totalKids = entries.reduce((sum, entry) => sum + entry.kids, 0);
  const totalAdults = entries.reduce((sum, entry) => sum + entry.adults, 0);

  // Small helpers for +/- steppers
  const stepKid = (delta) =>
    setKidInput((v) =>
      String(Math.max(0, parseInt(v || "0", 10) + delta || 0))
    );
  const stepAdult = (delta) =>
    setAdultInput((v) =>
      String(Math.max(0, parseInt(v || "0", 10) + delta || 0))
    );

  return (
    <div className="tracker">
      <div className="tracker-header">
        <h2>Birthday Party Check-In Tracker</h2>

        <div className="totals">
          <div className="total-chip kids">
            <span>Total Kids</span>
            <strong>{totalKids}</strong>
          </div>
          <div className="total-chip adults">
            <span>Total Adults</span>
            <strong>{totalAdults}</strong>
          </div>
        </div>
      </div>

      <div className="tracker-card">
        <div className="row">
          <div className="field">
            <label>Kids</label>
            <div className="stepper">
              <button
                type="button"
                onClick={() => stepKid(-1)}
                aria-label="Decrease kids"
              >
                −
              </button>
              <input
                type="number"
                min="0"
                value={kidInput}
                onChange={(e) => setKidInput(e.target.value)}
                placeholder="0"
              />
              <button
                type="button"
                onClick={() => stepKid(1)}
                aria-label="Increase kids"
              >
                +
              </button>
            </div>
          </div>

          <div className="field">
            <label>Adults</label>
            <div className="stepper">
              <button
                type="button"
                onClick={() => stepAdult(-1)}
                aria-label="Decrease adults"
              >
                −
              </button>
              <input
                type="number"
                min="0"
                value={adultInput}
                onChange={(e) => setAdultInput(e.target.value)}
                placeholder="0"
              />
              <button
                type="button"
                onClick={() => stepAdult(1)}
                aria-label="Increase adults"
              >
                +
              </button>
            </div>
          </div>
        </div>

        <div className="actions">
          <button className="btn-primary" onClick={handleAddEntry}>
            Add Entry
          </button>
          <button
            className="btn-danger"
            onClick={() => setShowResetModal(true)}
          >
            Reset Tracker
          </button>
        </div>
      </div>

      <div className="table-card">
        <h3>Party Check-Ins</h3>
        {entries.length === 0 ? (
          <p className="muted">No entries yet.</p>
        ) : (
          <div className="table-wrapper">
            <table className="tracker-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Kids</th>
                  <th>Adults</th>
                  <th className="center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const editing = isEditingId === entry.id;
                  return (
                    <tr key={entry.id}>
                      <td data-label="Time">{entry.time}</td>
                      <td data-label="Kids" className="center">
                        {editing ? (
                          <input
                            type="number"
                            min="0"
                            value={editKids}
                            onChange={(e) => setEditKids(e.target.value)}
                            className="cell-input"
                          />
                        ) : (
                          entry.kids
                        )}
                      </td>
                      <td data-label="Adults" className="center">
                        {editing ? (
                          <input
                            type="number"
                            min="0"
                            value={editAdults}
                            onChange={(e) => setEditAdults(e.target.value)}
                            className="cell-input"
                          />
                        ) : (
                          entry.adults
                        )}
                      </td>
                      <td className="center actions-cell">
                        {editing ? (
                          <>
                            <button
                              className="btn-small"
                              onClick={() => handleSaveEdit(entry.id)}
                            >
                              Save
                            </button>
                            <button
                              className="btn-small btn-ghost"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn-small"
                              onClick={() => handleEdit(entry)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-small btn-danger"
                              onClick={() => handleDelete(entry.id)}
                              title="Delete this row"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <strong>Totals</strong>
                  </td>
                  <td className="center">
                    <strong>{totalKids}</strong>
                  </td>
                  <td className="center">
                    <strong>{totalAdults}</strong>
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {showResetModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <h4>Reset Tracker</h4>
            <p>
              Are you sure you want to clear all entries and start a new party?
            </p>
            <div className="modal-actions">
              <button className="btn-danger" onClick={handleReset}>
                Yes, Reset
              </button>
              <button
                className="btn-primary"
                onClick={() => setShowResetModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tracker;
